/**
 * DynamicIcon — "Living" progress icon for Projects and Lists, with custom
 * icon override support.
 *
 * When `icon` is set (e.g. "pika:category/name" or "emoji:☕"), renders the
 * custom icon instead of the progress visualization.
 *
 * **Project** (type="project"): Circle with conic-gradient pie chart showing
 * completion percentage. Animates smoothly over 800ms when percentage changes.
 *
 * **List** (type="list"): Rounded square with horizontal bars that fade in/out
 * based on the number of unfinished items:
 *   - 0 unfinished → 0 bars (one faint bar shown)
 *   - ≤10 unfinished → 1 bar
 *   - ≤20 unfinished → 2 bars
 *   - ≥21 unfinished → 3 bars
 *
 * **Completed** state: Solid color background with white checkmark.
 *
 * Ported from v1's `TimelineToToDoIcon` component, rebuilt with
 * Tailwind-compatible inline styles (no MUI dependency).
 */

import { memo, useEffect, useRef, useState } from "react"
import CheckmarkIcon from "@/shared/icons/totodoicons/stroke/checkmark-1.svg?react"
import CrossIcon from "@/shared/icons/totodoicons/stroke/cross.svg?react"
import { ObjectStatus } from "@/db/schema"
import { ContainerIcon, parseIconString } from "@/shared/icons/resolveContainerIcon"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TimelineType = "project" | "list"
type TimelineSize = "xs" | "small" | "medium" | "large"

interface DynamicIconProps {
  /** "project" = circle with pie, "list" = rounded square with bars */
  type: TimelineType
  /** Completion percentage 0-100 (used for project pie chart) */
  percentage: number
  /** Number of unfinished child tasks (used for list bar logic) */
  unfinished: number
  /** Object completion status */
  status: ObjectStatus
  /** Custom icon string — overrides progress visualization when set */
  icon?: string
  /** Icon size preset */
  size?: TimelineSize
  /** Icon color — inherits from parent context via currentColor if not set */
  color?: string
  /** When false, percentage changes are applied instantly (no 800ms animation) */
  animate?: boolean
  /** Optional label rendered centered inside the inner circle (e.g. selection index) */
  label?: string | number
  className?: string
}

// ---------------------------------------------------------------------------
// Size presets — matching v1 proportions
// ---------------------------------------------------------------------------

interface SizeDef {
  outer: number
  inner: number
  padding: number
  border: number
}

const SIZES: Record<TimelineSize, SizeDef> = {
  xs: { outer: 16, inner: 8, padding: 2, border: 2 },
  small: { outer: 20, inner: 12, padding: 2, border: 2 },
  medium: { outer: 24, inner: 16, padding: 2, border: 2 },
  large: { outer: 34, inner: 22, padding: 3, border: 3 },
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DynamicIcon = memo(function DynamicIcon({
  type,
  percentage = 0,
  unfinished = 0,
  status,
  icon,
  size = "small",
  color = "currentColor",
  animate = true,
  label,
  className,
}: DynamicIconProps) {
  const s = SIZES[size]

  const isCompleted = status === ObjectStatus.Completed
  const isCanceled = status === ObjectStatus.Canceled
  const isDone = isCompleted || isCanceled

  // Animated percentage (project pie only).
  // When animate=false, use percentage directly — no state, no effect,
  // no extra repaint (avoids WebKit subpixel compositing artifacts).
  const safePercentage = isNaN(percentage) ? 0 : percentage
  const [animatedPct, setAnimatedPct] = useState(safePercentage)
  const prevPctRef = useRef(safePercentage)

  useEffect(() => {
    if (!animate) return
    if (isDone || type !== "project") {
      setAnimatedPct(safePercentage)
      prevPctRef.current = safePercentage
      return
    }

    const start = prevPctRef.current
    const end = safePercentage
    if (start === end) {
      setAnimatedPct(end)
      return
    }

    const duration = 800
    let startTime: number | null = null
    let rafId: number

    function tick(timestamp: number) {
      if (startTime === null) startTime = timestamp
      const elapsed = timestamp - startTime
      const progress = Math.min(elapsed / duration, 1)
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3)
      setAnimatedPct(start + (end - start) * eased)

      if (progress < 1) {
        rafId = requestAnimationFrame(tick)
      } else {
        setAnimatedPct(end)
      }
    }

    rafId = requestAnimationFrame(tick)
    prevPctRef.current = safePercentage
    return () => cancelAnimationFrame(rafId)
  }, [safePercentage, isDone, type, animate])

  const displayPct = animate ? animatedPct : safePercentage

  // Custom icon override — render pika/emoji instead of progress visualization
  // (placed after all hooks to satisfy React's rules of hooks)
  // Wrapped in a fixed-size span to prevent layout shift while pika SVG loads.
  // Skip custom icon when completed/canceled — show checkmark/X instead.
  if (icon && !isDone && parseIconString(icon)) {
    return (
      <span
        className={className}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", width: s.outer, height: s.outer, minWidth: s.outer, flexShrink: 0 }}
      >
        <ContainerIcon icon={icon} size={`${s.outer}px`} color={color} />
      </span>
    )
  }

  // -----------------------------------------------------------------------
  // Completed state — solid circle/square with white checkmark
  // -----------------------------------------------------------------------
  if (isCompleted) {
    return (
      <span
        className={className}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: s.outer,
          height: s.outer,
          minWidth: s.outer,
          borderRadius: type === "list" ? "25%" : "50%",
          background: color,
        }}
      >
        <CheckmarkIcon
          width={s.outer - 2}
          height={s.outer - 2}
          style={{ color: "var(--background)" }}
        />
      </span>
    )
  }

  // -----------------------------------------------------------------------
  // Canceled state — solid circle/square with white X
  // -----------------------------------------------------------------------
  if (isCanceled) {
    return (
      <span
        className={className}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: s.outer,
          height: s.outer,
          minWidth: s.outer,
          borderRadius: type === "list" ? "25%" : "50%",
          background: color,
        }}
      >
        <CrossIcon
          width={s.outer - 2}
          height={s.outer - 2}
          style={{ color: "var(--background)" }}
        />
      </span>
    )
  }

  // -----------------------------------------------------------------------
  // List type — rounded square with horizontal bars
  // -----------------------------------------------------------------------
  if (type === "list") {
    // Map unfinished count to visible bar count (0-3 bars of 5 positions)
    const barCount =
      unfinished <= 0 ? 0 : unfinished <= 10 ? 1 : unfinished <= 20 ? 2 : 3
    // Bars at positions 0, 2, 4 (three evenly spaced)
    const visiblePositions =
      barCount === 0
        ? [4]
        : barCount === 1
          ? [4]
          : barCount === 2
            ? [2, 4]
            : [0, 2, 4]

    return (
      <span
        className={className}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: s.outer,
          height: s.outer,
          minWidth: s.outer,
          border: `${s.border}px solid ${color}`,
          borderRadius: "25%",
          padding: s.padding,
          gap: 0,
          paddingTop: s.padding + 1, // nudge bars down slightly for better visual balance
          paddingBottom: s.padding + 1,
        }}
      >
        {Array.from({ length: 5 }, (_, i) => {
          const isVisible = visiblePositions.includes(i)
          const opacity = barCount === 0 && i === 4 ? 0.4 : isVisible ? 1 : 0
          // Staggered delay: bottom bar first, then up
          const orderedVisible = [...visiblePositions].sort((a, b) => b - a)
          const position = orderedVisible.indexOf(i)
          const delay = position >= 0 ? `${position * 0.5}s` : "0s"

          return (
            <span
              key={i}
              style={{
                width: "80%",
                height: "40%",
                borderRadius: "25%",
                background: color,
                opacity,
                transition: "opacity 0.5s ease-in-out",
                transitionDelay: delay,
              }}
            />
          )
        })}
      </span>
    )
  }

  // -----------------------------------------------------------------------
  // Project type — circle with conic-gradient pie chart
  // Note: Do NOT add overflow:hidden here — it triggers a known WebKit
  //       compositing bug (#140535) with border-radius clips, producing
  //       subpixel white artifacts on freshly painted layers. v1 never
  //       used overflow:hidden either.
  // -----------------------------------------------------------------------
  return (
    <span
      className={className}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: s.outer,
        height: s.outer,
        minWidth: s.outer,
        border: `${s.border}px solid ${color}`,
        borderRadius: "50%",
        padding: s.padding,
        isolation: "isolate",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: s.inner,
          height: s.inner,
          minWidth: s.inner,
          minHeight: s.inner,
          borderRadius: "50%",
          background:
            displayPct >= 100
              ? color
              : displayPct <= 0
                ? "transparent"
                : `conic-gradient(${color} ${displayPct}%, transparent ${displayPct}% 100%)`,
        }}
      >
        {label != null && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: s.inner,
              height: s.inner,
              fontSize:
                String(label).length >= 3
                  ? s.inner * 0.42
                  : String(label).length >= 2
                    ? s.inner * 0.52
                    : s.inner * 0.65,
              fontWeight: 700,
              lineHeight: 1,
              color: "rgba(255,255,255,0.5)",
              pointerEvents: "none",
              fontFamily: "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace",
              fontVariantNumeric: "tabular-nums",
              letterSpacing: String(label).length >= 2 ? "-0.03em" : undefined,
              paddingTop: "0.2em",
            }}
          >
            {label}
          </span>
        )}
      </span>
    </span>
  )
})
